import { Injectable, Injector } from '@angular/core';
import { AVATARS } from '@constants/miscellaneous';
import { IAsset, IAssetInfo } from '@interfaces/asset';
import { IAssetValues } from '@interfaces/assetValue';
import { IPortfolio } from '@interfaces/portfolios';
import { BehaviorSubject } from 'rxjs';

import { CASH_NEMO, USD_NEMO, RANKING_SHARD_SIZE, RANKING_LOAD_SIZE, RELATED_STOCKS_LIMIT } from '@constants/miscellaneous';
import { IAssetInvestors, IAssetRanking } from '@interfaces/asset';
import { IGeneralConstants } from '@interfaces/general-constants';
import { IAssetName } from '@interfaces/portfolio-constants';
import { IActivity } from '@interfaces/users';
import { ErrorAndLoadingService } from '../error-loading/error-loading.service';
import { FirebaseService } from '../firebase/firebase.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AssetsService {
  private assetValuesConstantsSource = new BehaviorSubject<IAssetValues>(null);
  currentAssetValues = this.assetValuesConstantsSource.asObservable();
  private favoriteAssetsSource = new BehaviorSubject<string[]>(null);
  currentFavoriteAssets = this.favoriteAssetsSource.asObservable();
  portfoliosAssetsSource: {[key: string]: any};
  generalConstants: IGeneralConstants;
  assetNames: IAssetName[];
  mostBoughtStocks: { rows: IAssetInvestors[] };
  rankingByStock: any;
  assetRankingAnswer: {[key: string]: IAssetRanking};
  displayAssetRankingProfiles: {[key: string]: any};
  loadingAssetInvestors: {[key: string]: boolean};
  lastFetchedRanking: {[key: string]: number};
  lastShardInitialPosition: {[key: string]: number};
  lastShowedRankingPositionIndex: {[key: string]: number};
  alreadyCalledMostBought: boolean;
  assetsRankingDocs: {[key: string]: any};
  private racionalStocksService: any;

  constructor(
    private firebaseService: FirebaseService,
    private errorService: ErrorAndLoadingService,
    private userService: UserService,
    private injector: Injector,
  ) {
    this.lastShardInitialPosition = {};
    this.lastShowedRankingPositionIndex = {};
    this.portfoliosAssetsSource = {};
    this.lastFetchedRanking = {};
    this.assetRankingAnswer = {};
    this.displayAssetRankingProfiles = {};
    this.loadingAssetInvestors = {};
    this.assetsRankingDocs = {};
    this.firebaseService.currentGeneralConstants.subscribe(generalConstants => this.generalConstants = generalConstants);
    this.firebaseService.currentPortfolioConstants.subscribe(portfolioConstants => {
      if (portfolioConstants) {
        this.assetNames = portfolioConstants.assetNames;
      } else {
        this.firebaseService.getGeneralConstants();
      }
    });
  }

  // Only for local portfolios
  async getPortfolioAssetsReturnsByPeriod(portfolio: IPortfolio) {
    // return empty if portfolio has no investments
    if (!portfolio.lastHistoricEvolution.effectivePortfolioComposition) { return {}; }
    // fetch AssetValues to use later
    if (!this.assetValuesConstantsSource.value) { await this.getAssetValues(); }
    // calculate the first day the portfolio had investments and the values of the assets in that date
    const portfolioEvolution = this.firebaseService.getCollection(`portfolios/${portfolio.id}/historicEvolution`);
    const initialDoc = await portfolioEvolution.orderBy('date', 'asc').limit(1).get();
    if (!initialDoc.docs[0]) { return {}; }
    const initialDate = initialDoc.docs[0].id;
    const initialDateAssetValuesSnapshot = await this.firebaseService.getCollection('assetValues').doc(initialDate).get();
    const initialAssetValues = (initialDateAssetValuesSnapshot.data() as IAssetValues).values;

    const currentActives = { 'CASH': {'Inicio': null, '1M': null, '12M': null, '1D': null} };
    const assetsValues = this.assetValuesConstantsSource.value.values;
    portfolio.lastHistoricEvolution.effectivePortfolioComposition.forEach(async (asset) => {
      const assetId = asset.assetId;
      if (assetId !== 'CASH') {
        currentActives[assetId] = {};
        const sharePrice = assetsValues.filter(e => e.assetId === assetId)[0].sharePrice;
        // calculate returns for 1M and 12M
        const assetPeriodsInfo = await this.getAssetPeriodsInfo(assetId);
        ['1M', '12M', '1D'].forEach(period => {
          // compare price is saved in the currency of the asset
          const comparePrice = assetPeriodsInfo.filter(e => e.period === period)[0].comparePrice;
          const assetReturn = Math.round((sharePrice / comparePrice - 1) * 10000) / 100;
          currentActives[assetId][period] = assetReturn;
        });
        // calculate returns from the start of the portfolio investments
        const initialValues = initialAssetValues.filter(e => e.assetId === assetId)[0];
        let beginningReturn = null;
        if (initialValues) {
          let beginningPrice = initialValues.sharePrice;
          const stockSplitMultiplier = await this.getStockSplitsMultiplier(assetId, initialDate);
          beginningPrice = beginningPrice / stockSplitMultiplier;
          beginningReturn = Math.round((sharePrice / beginningPrice - 1) * 10000) / 100;
        }
        currentActives[assetId]['Inicio'] = beginningReturn;
      }
    });
    return currentActives;
  }

  private async getRacionalStocksService() {
    if (!this.racionalStocksService) {
      const module = await import('../racional-stocks/racional-stocks.service');
      this.racionalStocksService = this.injector.get(module.RacionalStocksService);
    }
    return this.racionalStocksService;
  }

  async getStockSplitsMultiplier(assetId: string, initialDate: string): Promise<number> {
    let multiplier = 1;
    const assetInfo = await this.getAsset(assetId);
    if (assetInfo.stockSplits) {
      assetInfo.stockSplits.forEach(ss => {
        if (ss.date > initialDate) {
          multiplier = multiplier * ss.multiplier;
        }
      });
    }
    return multiplier;
  }

  async getAsset(id: string) {
    try {
      const collection = this.firebaseService.getCollection('assets');
      const assetSnapshot = await this.firebaseService.getDocument(
        id,
        collection,
      );
      return assetSnapshot as IAsset;
    } catch (err) {
      return null;
    }
  }

  async getAssets() {
    try {
      const collection = this.firebaseService.getCollection('assets');
      const assetSnapshot = await this.firebaseService.getAllDocuments(
        collection,
      );
      return assetSnapshot.docs.map(asset => asset.data() as IAsset);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener los datos, inténtalo nuevamente', false);
      throw err;
    }
  }

  async getPopularAssets() {
    try {
      let lastWorkingDate = new Date();
      let popularAssets = null;
      while (!popularAssets || !popularAssets.length) {
        const assetVolumesSnapshot = await this.firebaseService.getCollection('assetVolumes')
        .where('updatedAt', '<=', lastWorkingDate)
        .orderBy('updatedAt', 'desc').limit(1).get();
        if (assetVolumesSnapshot.docs[0]) {
          popularAssets = assetVolumesSnapshot.docs[0].data().values.filter(a => a.usersTransactions > 0);
          lastWorkingDate = this.firebaseService.getPreviousWorkingDate(lastWorkingDate, true, false);
        }
      }
      return popularAssets;
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener los datos, inténtalo nuevamente', false);
      throw err;
    }
  }

  async getMarketAssets() {
    while (!this.assetNames) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    try {
      return this.assetNames.filter(a => a.showInMarkets);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener los datos, inténtalo nuevamente', false);
      throw err;
    }
  }

  async getDWAssets() {
    while (!this.assetNames) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    try {
      return this.assetNames.filter(a => a.drivewealthAsset && a.showInApp);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener los datos, inténtalo nuevamente', false);
      throw err;
    }
  }

  getAssetInfo(assetId: string) {
    return this.assetNames.find(a => a.assetId === assetId);
  }

  async getAssetValues() {
    if (this.assetValuesConstantsSource.value) { return; }
    try {
      const collection = await this.firebaseService
        .getCollection('assetValues')
        .orderBy('updatedAt', 'desc')
        .limit(1)
        .get();
      this.firebaseService.getDocumentObs(
        'assetValues',
        collection.docs[0].id,
        this.assetValuesConstantsSource,
      );
    } catch (err) {
      this.firebaseService.logError(`Error al obtener los datos: ${err}`);
      throw err;
    }
  }

  async getAssetPeriodsInfo(id: string): Promise<IAssetInfo[] | null> {
    if (!id) { return; }
    try {
      const collection = this.firebaseService.getCollection(`assets/${id}/assetInfo`);
      const periodsInfo = await this.firebaseService.getDocumentObject('periodsInfo', collection);
      return periodsInfo.data().assetPeriodsInfo;
    } catch (err) {
      return null;
    }
  }

  buildAssetIconURL(assetId: string, isStock: boolean): string {
    if (this.generalConstants && assetId !== CASH_NEMO && assetId !== USD_NEMO) {
      if (!isStock) {
        return `https://storage.googleapis.com/racional-images/assets/${assetId}.svg`;
      }
      const assetInAssetNames = this.assetNames.find(a => a.assetId === assetId);
      if (assetInAssetNames && assetInAssetNames.overrideIconUrl) {
        return assetInAssetNames.overrideIconUrl;
      }
      return this.generalConstants.drivewealth.assetIconBaseURL + assetId.toLowerCase() + '.png';
    }
  }

  getAssetName(assetId: string): string {
    if (this.assetNames) {
      const asset = this.assetNames.find(a => a.assetId === assetId);
      return asset && asset.assetClass;
    }
  }

  getAssetTicker(assetId: string): string {
    if (this.assetNames) {
      const asset = this.assetNames.find(a => a.assetId === assetId);
      return asset ? asset.ticker : assetId;
    }
    return assetId;
  }

  getAssetDwTemp(assetId: string): boolean {
    if (this.assetNames) {
      if (!this.assetNames.find(a => a.assetId === assetId)) {
        return;
      }
      return this.assetNames.find(a => a.assetId === assetId).dwTemp;
    }
  }

  async getAssetAdvancedStats(assetId: string, isDrivewealth: boolean): Promise<any | null> {
    if (!isDrivewealth) { return null; }
    try {
      const collection = this.firebaseService.getCollection(`assets/${assetId}/assetInfo`);
      const advancedStats = await this.firebaseService.getDocumentObject('advancedStats', collection);
      return advancedStats.data();
    } catch (err) {
      return null;
    }
  }

  async getMostBoughtStocks(forceFetch?: boolean) {
    if (this.alreadyCalledMostBought) {
      while (!this.mostBoughtStocks) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.mostBoughtStocks;
    }
    if ((!this.mostBoughtStocks && !this.alreadyCalledMostBought) || forceFetch) {
      this.alreadyCalledMostBought = true;
      this.mostBoughtStocks = await this.getMostBoughtStocksFn();
      this.alreadyCalledMostBought = false;
    }
    return this.mostBoughtStocks;
  }

  async getMostBoughtStocksFn() {
    const assetMostBoughtSnapshot = await this.firebaseService.getCollection('assetsMostBought')
    .orderBy('date', 'desc').limit(1).get()
    .catch(() => {
      return null;
    });
    if (!assetMostBoughtSnapshot || !assetMostBoughtSnapshot.size) {
      return { rows: [] };
    }
    const assetsMostBough = assetMostBoughtSnapshot.docs[0].data().values;
    const assetsMostBoughDate = assetMostBoughtSnapshot.docs[0].data().date;
    return { rows: assetsMostBough };
  }

  async getAssetRanking(assetId: string): Promise<IAssetRanking> {
    if (!assetId) { return; }
    if (this.assetRankingAnswer[assetId] && Date.now() - this.lastFetchedRanking[assetId] < 180000) {
      return this.assetRankingAnswer[assetId];
    }
    if (this.loadingAssetInvestors[assetId]) {
      while (this.loadingAssetInvestors[assetId]) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.assetRankingAnswer[assetId];
    }
    this.loadingAssetInvestors[assetId] = true;
    this.lastShowedRankingPositionIndex[assetId] = 0;
    this.lastShardInitialPosition[assetId] = 0;
    const getRankingByAssetFn = this.firebaseService.functions.httpsCallable('AssetCalculateInvestorsShards');
    const response = await getRankingByAssetFn({assetId: assetId, lastShardInitialPosition: this.lastShardInitialPosition[assetId]});
    this.assetRankingAnswer[assetId] = response.data;
    this.loadingAssetInvestors[assetId] = false;
    this.lastShardInitialPosition[assetId] = response.data.lastShardInitialPosition;
    this.lastFetchedRanking[assetId] = Date.now();
    return this.assetRankingAnswer[assetId];
  }

  async loadMoreProfiles(assetId: string) {
    let displayInvestorsRanking;

    if (this.lastShowedRankingPositionIndex[assetId] < RANKING_SHARD_SIZE) {
      displayInvestorsRanking = this.assetRankingAnswer[assetId].ranking;
    } else {
      const getRankingByAssetFn = this.firebaseService.functions.httpsCallable('AssetCalculateInvestorsShards');
      const response = await getRankingByAssetFn({assetId: assetId, lastShardInitialPosition: this.lastShardInitialPosition[assetId]});
      this.assetRankingAnswer[assetId] = response.data;
      this.lastShardInitialPosition[assetId] = response.data.lastShardInitialPosition;
      this.lastFetchedRanking[assetId] = Date.now();
      displayInvestorsRanking = this.assetRankingAnswer[assetId].ranking;
      this.lastShowedRankingPositionIndex[assetId] = 0;
    }
    const profileRandomNames = this.generalConstants.profileRandomNames;
    const investorsArray =
    displayInvestorsRanking.values.slice(
      this.lastShowedRankingPositionIndex[assetId],
      this.lastShowedRankingPositionIndex[assetId] + RANKING_LOAD_SIZE)
      ;
    this.lastShowedRankingPositionIndex[assetId] += RANKING_LOAD_SIZE;
    for (const investor of investorsArray) {
      let profile = null;
      if (investor.profileId) {
        profile = await this.getProfile(investor.profileId);
      }
      if (!profile) {
        const hash = Math.floor(investor.hashedId || (Math.random() * 10000000000));
        const nameAnimal = profileRandomNames.animal[hash % profileRandomNames.animal.length];
        const nameColor = profileRandomNames.color[hash % profileRandomNames.color.length];
        profile = {
          profileId: null,
          username: null,
          displayName: `${nameAnimal} ${nameColor}`,
          profileImage: AVATARS[hash % 12],
        };
      }
      this.displayAssetRankingProfiles[assetId].push({profile, ranking: investor.position, ownProfile: false});
    }
    return this.displayAssetRankingProfiles[assetId];
  }

  async setProfilesRanking(assetId: string) {
    if (!(assetId in this.displayAssetRankingProfiles) ) {
      this.displayAssetRankingProfiles[assetId] = [];
      return await this.loadMoreProfiles(assetId);
    }
    return this.displayAssetRankingProfiles[assetId];
  }

  async setOwnProfileRanking(assetId: string, profileId: string) {
    if (!this.assetRankingAnswer[assetId]) {
      await this.getAssetRanking(assetId);
    }
    const profile = await this.getProfile(profileId);
    const ownProfile = {
      position30days: this.assetRankingAnswer[assetId].position30Days,
      profile: profile,
      ranking: this.assetRankingAnswer[assetId].currentPosition,
    };
    return ownProfile;
  }

  async getAssetRankingDoc(assetId: string) {
    if (!assetId) { return 0; }
    if (!this.assetsRankingDocs[assetId]) {
      const assetRankingDoc = await this.firebaseService.getCollection(`assets/${assetId}/rankings`)
        .orderBy('date', 'desc')
        .limit(1)
        .get();
      this.assetsRankingDocs[assetId] = assetRankingDoc.docs.length ? assetRankingDoc.docs[0].data() : null;
    }
    return this.assetsRankingDocs[assetId];
  }

  async getProfile(profileId: string) {
    return this.userService.getSocialProfileInformation(profileId).then(profile => {
      if (!profile) {
        return false;
      }
      const profileImage = profile.profileImage ? profile.profileImage : AVATARS[profile.animalImage];
      return {profileId, username: profile.username, displayName: profile.displayName, profileImage};
    });
  }

  async getGroupOfAssetActivities(assetId: string, lastDocRef?) {
    let query = this.firebaseService.getCollection(`assets/${assetId}/activities`).orderBy('date', 'desc');
    query = lastDocRef ? query.startAfter(lastDocRef) : query;
    return query.limit(10).get().then(activitiesRef => {
      const activities = activitiesRef.docs.map(doc => ({ ...doc.data(), date: doc.data().date.toDate() } as IActivity));
      return { activities, lastDocRef: activitiesRef.docs[activitiesRef.docs.length - 1] };
    });
  }

  async getFavoriteAssets(userId: string): Promise<string[]> {
    try {
      const collection = this.firebaseService.getCollection(`users/${userId}/favorites`);
      const favoriteAssetsSnapshot = await this.firebaseService.getAllDocuments(
        collection,
      );
      if (!favoriteAssetsSnapshot) {
        return [];
      }
      if (!favoriteAssetsSnapshot.docs[0]) {
        this.favoriteAssetsSource.next([]);
        return [];
      }
      const favoriteAssets = favoriteAssetsSnapshot.docs[0].data().assets;
      this.favoriteAssetsSource.next(favoriteAssets ? favoriteAssets : []);
      return favoriteAssets;
    } catch (err) {
    }
  }

  async addFavoriteAssets(userId: string, assetId: string) {
    this.firebaseService.logEvent(`clicked_add_favorite_${assetId}_pro`, {});
    const favoritesArray = this.favoriteAssetsSource.value || [];
    favoritesArray.push(assetId);
    this.favoriteAssetsSource.next(favoritesArray);
    this.firebaseService.firestore.doc(`users/${userId}/favorites/favorites`).get()
    .then((doc) => {
      if (doc && doc.exists) {
        const newList = [...(doc.data().assets || []), assetId];
        this.firebaseService.firestore.doc(`users/${userId}/favorites/favorites`).update({ assets: newList, updatedAt: new Date() })
        .then(() => {
          this.errorService.presentToastWithDuration(3000, 'top',
            `Agregaste ${assetId} a tus Favoritos. Recibirás notificaciones automáticas de sus variaciones de precio`, false);
        })
        .catch(() => {
          this.errorService.presentToastWithDuration(1000, 'top', 'Error al actualizar favoritos, intentalo nuevamente', false);
        });
      } else {
        this.firebaseService.firestore.doc(`users/${userId}/favorites/favorites`).set({ assets: [assetId], createdAt: new Date() })
        .then(() => {
          this.errorService.presentToastWithDuration(3000, 'top',
            `Agregaste ${assetId} a tus Favoritos. Recibirás notificaciones automáticas de sus variaciones de precio`, false);
        })
        .catch(() => {
          this.errorService.presentToastWithDuration(1000, 'top', 'Error al actualizar favoritos, intentalo nuevamente', false);
        });
      }
    })
    .catch((err) => {
      this.errorService.presentToastWithDuration(1000, 'top', 'Error al actualizar favoritos, intentalo nuevamente', false);
    });
  }

  async removeFavoriteAssets(userId: string, assetId: string) {
    this.firebaseService.logEvent(`clicked_remove_favorite_${assetId}_pro`, {});
    let favoritesArray = this.favoriteAssetsSource.value || [];
    favoritesArray = favoritesArray.filter((a) => a !== assetId);
    this.favoriteAssetsSource.next(favoritesArray);
    this.firebaseService.firestore.doc(`users/${userId}/favorites/favorites`).get()
    .then((doc) => {
      if (doc) {
        const newList = (doc.data().assets || []).filter((a) => a !== assetId);
        this.firebaseService.firestore.doc(`users/${userId}/favorites/favorites`).update({ assets: newList, updatedAt: new Date() })
        .then(() => {
          this.errorService.presentToastWithDuration(1000, 'top', `Quitaste ${assetId} de tus Favoritos`, false);
        })
        .catch(() => {
          this.errorService.presentToastWithDuration(1000, 'top', 'Error al actualizar favoritos, intentalo nuevamente', false);
        });
      } else {
        this.errorService.presentToastWithDuration(1000, 'top', 'Error al actualizar favoritos, intentalo nuevamente', false);
      }
    })
    .catch(() => {
      this.errorService.presentToastWithDuration(1000, 'top', 'Error al actualizar favoritos, intentalo nuevamente', false);
    });
  }

  public deleteFavoriteAssets() {
    this.favoriteAssetsSource.next(null);
  }

  async getFX(isSell: boolean, isPro: boolean) {
    return this.firebaseService.firestore.doc(`forex/CLP_USD`).get()
    .then(fxDoc => {
      const fx = fxDoc.data();
      const price = isSell ? fx.bid : fx.ask;
      const spread = this.generalConstants.drivewealth[(isSell ? 'USD_to_CLP' : 'CLP_to_USD') + '_spread' + (isPro ? '_pro' : '')];
      return price * spread;
    });
  }

  async getRelatedAssetsWithFallback(assetId: string, limit: number = RELATED_STOCKS_LIMIT) {
    const dwAssets = await this.getDWAssets();
    const racionalStocksService = await this.getRacionalStocksService();
    const relatedStocksResponse = await racionalStocksService.getRelatedAssets(assetId, limit);
    let relatedStocks = [];

    if (relatedStocksResponse && relatedStocksResponse.results && relatedStocksResponse.results.length > 0) {
      relatedStocksResponse.results.forEach(relatedAssetId => {
        const stock = dwAssets.find(s => s.assetId === relatedAssetId);
        if (stock) {
          relatedStocks.push(stock);
        }
      });
    }

    if (relatedStocks.length === 0 || relatedStocksResponse.isFallback) {
      const mostBoughtResponse = await this.getMostBoughtStocks();
      if (mostBoughtResponse && mostBoughtResponse.rows && mostBoughtResponse.rows.length > 0) {
        const mostBoughtStocks = mostBoughtResponse.rows
          .filter(asset => asset.assetId !== assetId)
          .slice(0, limit)
          .map(asset => dwAssets.find(s => s.assetId === asset.assetId))
          .filter(stock => stock);
        relatedStocks = mostBoughtStocks;
      }
    }

    return relatedStocks;
  }
}
